/**
 * AttachmentChips — horizontal chip row for pending attachments in the composer.
 *
 * Shows: image thumbnail (if available) or a file-type icon, filename truncated,
 * upload state text with spinner during uploading/scanning, remove (×) button.
 *
 * Design (`docs/GardEn/handoff-2026-06/garden/screens-chat.jsx`):
 * - Chips appear above the input row.
 * - --g-* tokens for all colors and radii.
 * - ≥44px remove button tap target.
 * - `aria-live="polite"` on the state text.
 * - `active:scale-95` on the remove button.
 * - Reduced motion: spinner does not render when `prefers-reduced-motion: reduce`.
 *
 * Icon substitutions (vs. plan): `image`/`fileText`/`sheet`/`loader` are not in
 * the @garden/ui Icon registry. We use `clip` (Paperclip) as the generic
 * file icon for pdf/xlsx/csv, and a CSS border spinner for the busy state.
 */
import { Icon } from "@garden/ui";
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type { AttachmentDraft } from "@/features/chat/attachment-types";

export interface AttachmentChipsProps {
  drafts: AttachmentDraft[];
  onRemove: (localId: string) => void;
}

/**
 * A minimal CSS-only spinner that respects `prefers-reduced-motion`.
 * Does not rely on a `loader` Icon (not in the icon registry).
 */
function Spinner(): ReactElement {
  return (
    <span
      className="inline-block size-3.5 rounded-full border-2 border-border border-t-primary motion-safe:animate-spin"
      aria-hidden="true"
    />
  );
}

export function AttachmentChips({
  drafts,
  onRemove,
}: AttachmentChipsProps): ReactElement | null {
  const { t } = useTranslation("chat");

  if (drafts.length === 0) return null;

  return (
    /* No own width/padding/margin any more: the chips render INSIDE the
       composer surface now, which already owns the measure and the gutter.
       Keeping `mx-auto max-w-[720px] px-4` here double-padded them and pinned
       them to a different measure than the field above. */
    <ul
      className="flex flex-wrap gap-2"
      aria-label={t("attach.listLabel", { defaultValue: "Attachments" })}
    >
      {drafts.map((d) => {
        const busy = d.state === "uploading" || d.state === "scanning";
        const isError = d.state === "error";

        return (
          <li
            key={d.localId}
            data-state={d.state}
            className={
              "flex items-center gap-2 rounded-[10px] border py-1.5 pl-1.5 pr-0.5 text-[15px]/[1.429] " +
              (isError
                ? "border-[var(--g-error)]/50 bg-[color-mix(in_srgb,var(--g-error)_7%,transparent)]"
                : "border-[var(--g-rule)] bg-[var(--g-sunken)]")
            }
          >
            {/* Thumbnail or file-type icon */}
            {d.thumbDataUrl !== null ? (
              <img
                src={d.thumbDataUrl}
                alt=""
                aria-hidden="true"
                className="size-8 rounded object-cover"
              />
            ) : (
              <span className="grid size-8 shrink-0 place-items-center rounded bg-muted text-muted-foreground">
                {/* clip (Paperclip) used for all non-image types */}
                <Icon name="clip" size={16} />
              </span>
            )}

            {/* Filename */}
            <span className="max-w-[10rem] truncate text-foreground">
              {d.file.name}
            </span>

            {/* State indicator — shown only while it says something. A chip
                that is READY is self-evidently ready; stamping "Готово" on
                every one of five attachments is noise that crowds out the
                filenames, which are the part the user actually reads. Busy and
                error states still announce themselves. */}
            {busy || isError ? (
              <span
                className={
                  "flex items-center gap-1.5 whitespace-nowrap text-[13px]/[1.333] " +
                  (isError ? "text-[var(--g-error)]" : "text-[var(--g-ink-2)]")
                }
                aria-live="polite"
              >
                {busy && <Spinner />}
                {isError
                  ? t("attach.error", { defaultValue: "Error" })
                  : t(`attach.${d.state}`, { defaultValue: d.state })}
              </span>
            ) : (
              <span className="sr-only" aria-live="polite">
                {t("attach.ready", { defaultValue: "Ready" })}
              </span>
            )}

            {/* Remove button — ≥44px tap target */}
            <button
              type="button"
              onClick={() => onRemove(d.localId)}
              aria-label={t("attach.removeAria", {
                name: d.file.name,
                defaultValue: `Remove ${d.file.name}`,
              })}
              className="grid size-9 shrink-0 place-items-center rounded-[8px] text-[var(--g-ink-3)] transition-[color,background-color,transform] duration-150 hover:bg-[var(--g-elevated)] hover:text-foreground active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] motion-reduce:transition-none"
            >
              <Icon name="x" size={16} />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export default AttachmentChips;
